import {
  ANALYTICS_RANGES,
  ANALYTICS_TABS,
  type AnalyticsRange,
  type AnalyticsTab,
} from "@lafayette/schemas";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import { DEFAULT_RANGE } from "./overview-model";

// The tab and the range of the cockpit live in the address (`?tab=events&range=90d`), so a link, a reload and the back button keep the view. A
// value that is not on the closed lists is the default, never an error.

export type CockpitAddress = Readonly<{
  tab: AnalyticsTab;
  range: AnalyticsRange;
  setRange: (range: AnalyticsRange) => void;
  /** The address of a tab of this customer, with the range kept. */
  tabHref: (tab: AnalyticsTab) => string;
}>;

const asTab = (value: string | null): AnalyticsTab =>
  (ANALYTICS_TABS as readonly string[]).includes(value ?? "")
    ? (value as AnalyticsTab)
    : "overview";
const asRange = (value: string | null): AnalyticsRange =>
  (ANALYTICS_RANGES as readonly string[]).includes(value ?? "")
    ? (value as AnalyticsRange)
    : DEFAULT_RANGE;

export function useCockpitAddress(): CockpitAddress {
  const [search, setSearch] = useSearchParams();
  const tab = asTab(search.get("tab"));
  const range = asRange(search.get("range"));
  const setRange = useCallback(
    (next: AnalyticsRange) =>
      setSearch(
        (current) => {
          const copy = new URLSearchParams(current);
          copy.set("range", next);
          // A zoom belongs to the range it was made in, and so does the interval: Hour is not allowed for a long range, and the server picks one
          // that fits the new window.
          copy.delete("from");
          copy.delete("to");
          copy.delete("interval");
          return copy;
        },
        { replace: true },
      ),
    [setSearch],
  );
  const tabHref = useCallback((to: AnalyticsTab) => `?tab=${to}&range=${range}`, [range]);
  return useMemo(() => ({ tab, range, setRange, tabHref }), [tab, range, setRange, tabHref]);
}
